import React, { useRef, useState } from "react";
import { Form, Input, Button, message } from "antd";
import { useNavigate } from "react-router-dom";
import { getSEOData } from "../../../global/utils/constants/seoConfig";
import SEO from "../../../global/components/SEO/SEO";
import sicunetImage from "../../../assets/images/request-demo-bg.jpg";
import { API_URL } from "../../../lib/apiConstants";
import HCaptcha from "@hcaptcha/react-hcaptcha";

const RequestDemoPage = () => {
  const [form] = Form.useForm();
  const [userType, setUserType] = useState("dealer");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();
  const captchaRef = useRef(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  // Get SEO data
  const seoData = getSEOData("/request-demo");

  const onFinish = async (values) => {
    if (!captchaToken) {
      message.error("Please complete the captcha.");
      return;
    }

    setLoading(true);

    try {
      // Prepare payload based on user type
      let payload = {
        user_type: userType, // Use the state value, not form value
        email: values.email,
        phone: values.phone,
        captcha_token: captchaToken,
      };

      if (userType === "dealer") {
        payload.name = values.dealerName; // API expects "name" field
        payload.company_name = values.company_name;
      } else {
        payload.name = values.endUserName; // API expects "name" field
      }

      const response = await fetch(`${API_URL}/send-mail`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        message.success("Demo request submitted successfully!");
        setSubmitted(true);
        form.resetFields();
        captchaRef.current?.resetCaptcha();
        setCaptchaToken(null);
      } else {
        message.error(
          data.message || "Failed to submit demo request. Please try again.",
        );
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      message.error("Something went wrong. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleNewRequest = () => {
    setSubmitted(false);
  };

  // Right Side Image Component (Reusable)
  const RightSideImage = () => (
    <div className="hidden lg:block w-full lg:w-1/2 h-[300px] lg:h-auto relative flex items-center justify-center overflow-hidden order-first lg:order-last">
      <div className="absolute inset-0">
        <img
          src={sicunetImage}
          alt="Sicunet Access Control"
          className="w-full h-full object-cover"
        />
      </div>

      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.2"%3E%3Ccircle cx="30" cy="30" r="1"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
        }}
      />
    </div>
  );

  if (submitted) {
    return (
      <>
        <SEO
          title={seoData.title}
          description={seoData.description}
          keywords={seoData.keywords}
          type="website"
        />
        <div
          className="
    min-h-screen flex items-center justify-center p-4
    bg-cover bg-center bg-no-repeat
  "
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url(${sicunetImage})`,
          }}
        >
          <div className="bg-white rounded-lg shadow-xl p-6 sm:p-8 mt-7 max-w-sm w-full text-center mx-4">
            {/* Green Circle with Checkmark */}
            <div className="flex justify-center mb-6 sm:mb-8">
              <div
                className="relative flex items-center justify-center"
                style={{
                  width: "100px",
                  height: "100px",
                  borderRadius: "50%",
                  backgroundColor: "rgba(139, 195, 74, 0.2)",
                }}
              >
                <div
                  className="flex items-center justify-center"
                  style={{
                    width: "75px",
                    height: "75px",
                    borderRadius: "50%",
                    backgroundColor: "#8BC34A",
                  }}
                >
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
              </div>
            </div>

            <h1 className="text-2xl md:text-4xl font-bold text-[#121212] mb-3 sm:mb-4 font-['Poppins']">
              Thank you!
            </h1>

            <p className="text-sm md:text-base text-[#4A4C56] mb-6 sm:mb-8 font-['Poppins'] leading-relaxed px-2 sm:px-4">
              Your demo request has been submitted. Our sales team will reach
              out to you shortly.
            </p>

            <Button
              type="primary"
              size="large"
              onClick={handleNewRequest}
              block
              className="rounded font-medium font-['Poppins']"
              style={{
                backgroundColor: "#007BFF",
                borderColor: "#007BFF",
                height: "42px",
                fontSize: "15px",
              }}
            >
              Close
            </Button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        type="website"
      />
      <div
        className="min-h-screen flex items-center justify-center px-4 sm:px-6 md:px-12 lg:px-24 py-8 sm:py-12 lg:pt-16 bg-cover bg-center"
        style={{ backgroundImage: `url(${sicunetImage})` }}
      >
        <div className="bg-white mt-[84px]  rounded-lg shadow-xl max-w-[560px] w-full mx-auto">
          <h1 className="text-center font-bold text-[#121212] text-2xl sm:text-3xl lg:text-[32px] pt-4 sm:pt-6 font-['Poppins'] leading-tight mb-1 px-4">
            Experience Neptune 2.0
          </h1>
          <p className="text-sm sm:text-base text-gray-700 mb-4 mt-3 sm:mt-5 lg:mb-6 text-center font-['Poppins'] px-4">
            Please fill out the form below to request a demo.
          </p>

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{ userType: "dealer" }}
            requiredMark={false}
          >
            <Form.Item name="userType">
              <div className="flex justify-center px-4">
                <div className="inline-flex bg-gray-100 rounded-lg p-1 w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={() => {
                      setUserType("dealer");
                      form.resetFields([
                        "dealerName",
                        "endUserName",
                        "company_name",
                        "email",
                        "phone",
                      ]);
                    }}
                    className={`flex-1 sm:flex-none px-6 sm:px-8 py-2.5 rounded-md text-sm font-semibold transition-all duration-200 font-['Poppins'] ${
                      userType === "dealer"
                        ? "bg-white text-[#016FFC] shadow-sm"
                        : "text-[#6B7280] hover:text-[#4B5563]"
                    }`}
                  >
                    Dealer
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setUserType("endUser");
                      form.resetFields([
                        "dealerName",
                        "endUserName",
                        "company_name",
                        "email",
                        "phone",
                      ]);
                    }}
                    className={`flex-1 sm:flex-none px-6 sm:px-8 py-2.5 rounded-md text-sm font-semibold transition-all duration-200 font-['Poppins'] ml-1 ${
                      userType === "endUser"
                        ? "bg-white text-[#016FFC] shadow-sm"
                        : "text-[#6B7280] hover:text-[#4B5563]"
                    }`}
                  >
                    End User
                  </button>
                </div>
              </div>
            </Form.Item>
            <div className="px-4 sm:px-6 py-2">
              {/* Fixed height container to prevent layout shift */}
              <div
                style={{ minHeight: userType === "dealer" ? "160px" : "80px" }}
              >
                {userType === "dealer" ? (
                  <>
                    <Form.Item
                      name="dealerName"
                      label={
                        <span className="text-sm font-medium  text-[#374151] font-['Poppins']">
                          Dealer Name
                        </span>
                      }
                      rules={[
                        { required: true, message: "Please enter dealer name" },
                      ]}
                    >
                      <Input
                        placeholder="Write here"
                        size="large"
                        className="rounded-md border-gray-250 hover:border-[#016FFC] focus:border-[#016FFC] shadow-sm"
                        style={{
                          height: "40px",
                          fontSize: "15px",
                          backgroundColor: "white",
                        }}
                      />
                    </Form.Item>

                    <Form.Item
                      name="company_name"
                      label={
                        <span className="text-sm font-medium text-[#374151] font-['Poppins']">
                          Company Name
                        </span>
                      }
                      rules={[
                        {
                          required: true,
                          message: "Please enter company name",
                        },
                      ]}
                    >
                      <Input
                        placeholder="Write here"
                        size="large"
                        className="rounded-md border-gray-250 hover:border-[#016FFC] focus:border-[#016FFC] shadow-sm"
                        style={{
                          height: "40px",
                          fontSize: "15px",
                          backgroundColor: "white",
                        }}
                      />
                    </Form.Item>
                  </>
                ) : (
                  <Form.Item
                    name="endUserName"
                    label={
                      <span className="text-sm font-medium text-[#374151] font-['Poppins']">
                        End User Name
                      </span>
                    }
                    rules={[
                      { required: true, message: "Please enter your name" },
                    ]}
                  >
                    <Input
                      placeholder="Write here"
                      size="large"
                      className="rounded-md border-gray-250 hover:border-[#016FFC] focus:border-[#016FFC] shadow-sm"
                      style={{
                        height: "40px",
                        fontSize: "15px",
                        backgroundColor: "white",
                      }}
                    />
                  </Form.Item>
                )}
              </div>

              <Form.Item
                name="phone"
                label={
                  <span className="text-sm font-medium text-[#374151] font-['Poppins']">
                    Phone
                  </span>
                }
                rules={[
                  { required: true, message: "Please enter your phone number" },
                ]}
              >
                <Input
                  placeholder="Write here"
                  size="large"
                  type="tel"
                  className="rounded-md border-gray-250 hover:border-[#016FFC] focus:border-[#016FFC] shadow-sm"
                  style={{
                    height: "40px",
                    fontSize: "15px",
                    backgroundColor: "white",
                  }}
                />
              </Form.Item>

              <Form.Item
                name="email"
                label={
                  <span className="text-sm font-medium text-[#374151] font-['Poppins']">
                    Email
                  </span>
                }
                rules={[
                  { required: true, message: "Please enter your email" },
                  {
                    type: "email",
                    message: "Please enter a valid email address",
                  },
                ]}
              >
                <Input
                  placeholder="Write here"
                  size="large"
                  type="email"
                  className="rounded-md border-gray-250 hover:border-[#016FFC] focus:border-[#016FFC] shadow-sm"
                  style={{
                    height: "40px",
                    fontSize: "15px",
                    backgroundColor: "white",
                  }}
                />
              </Form.Item>
              <Form.Item>
                <HCaptcha
                  ref={captchaRef}
                  sitekey="231048d6-eb42-4a90-ba0e-7e3ed338da68"
                  onVerify={(token) => setCaptchaToken(token)}
                  onExpire={() => setCaptchaToken(null)}
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  block
                  loading={loading}
                  disabled={!captchaToken}
                  className="rounded-md mt-2 font-semibold font-['Poppins'] shadow-md hover:shadow-lg transition-all duration-300 px-6"
                  style={{
                    backgroundColor: "#016FFC",
                    borderColor: "#016FFC",
                    height: "40px",
                    fontSize: "15px",
                  }}
                >
                  {loading ? "Submitting..." : "Request Demo"}
                </Button>
              </Form.Item>
            </div>
            <p className="text-center text-xs text-[#7d8080] pb-4 sm:pb-6 lg:mb-0 font-['Poppins'] px-4">
              We'll get back to you within 24 hours
            </p>
          </Form>
        </div>
      </div>
    </>
  );
};

export default RequestDemoPage;
